import React, { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import './AdminAvailability.css';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' }
];

function AdminAvailability() {
  const [activeTab, setActiveTab] = useState('weekly'); // 'weekly' or 'calendar'
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Calendar state
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateAvailability, setDateAvailability] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    loadWeeklySchedule();
    loadDateAvailability();
  }, []);

  const loadWeeklySchedule = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_weekly_schedule')
        .select('*')
        .order('day_of_week');

      if (error) throw error;

      const schedule = {};
      DAYS_OF_WEEK.forEach(day => {
        const dayData = data?.find(d => d.day_of_week === day.value);
        schedule[day.value] = {
          timeSlots: dayData?.time_slots || [],
          isActive: dayData?.is_active !== false
        };
      });

      setWeeklySchedule(schedule);
    } catch (error) {
      console.error('Error loading weekly schedule:', error);
      setMessage({ type: 'error', text: 'Failed to load weekly schedule' });
    } finally {
      setLoading(false);
    }
  };

  const loadDateAvailability = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_date_availability')
        .select('*')
        .order('availability_date');

      if (error) throw error;

      const availability = {};
      data?.forEach(item => {
        availability[item.availability_date] = {
          timeSlots: item.time_slots || [],
          isAvailable: item.is_available !== false
        };
      });

      setDateAvailability(availability);
    } catch (error) {
      console.error('Error loading date availability:', error);
    }
  };

  const addTimeSlot = (dayOfWeek) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        timeSlots: [...(prev[dayOfWeek]?.timeSlots || []), { start: '09:00', end: '17:00' }]
      }
    }));
  };

  const removeTimeSlot = (dayOfWeek, index) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        timeSlots: prev[dayOfWeek].timeSlots.filter((_, i) => i !== index)
      }
    }));
  };

  const updateTimeSlot = (dayOfWeek, index, field, value) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        timeSlots: prev[dayOfWeek].timeSlots.map((slot, i) =>
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const toggleDayActive = (dayOfWeek) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        isActive: !prev[dayOfWeek]?.isActive
      }
    }));
  };

  const saveWeeklySchedule = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      for (const day of DAYS_OF_WEEK) {
        const dayData = weeklySchedule[day.value];
        if (!dayData) continue;

        const { error } = await supabase
          .from('admin_weekly_schedule')
          .upsert({
            day_of_week: day.value,
            time_slots: dayData.timeSlots,
            is_active: dayData.isActive
          }, {
            onConflict: 'day_of_week'
          });

        if (error) throw error;
      }

      setMessage({ type: 'success', text: 'Weekly schedule saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving weekly schedule:', error);
      setMessage({ type: 'error', text: 'Failed to save weekly schedule' });
    } finally {
      setSaving(false);
    }
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const handleDateClick = (date) => {
    if (!date) return;
    const dateStr = date.toISOString().split('T')[0];
    setSelectedDate(dateStr);
    
    // Initialize if not exists
    if (!dateAvailability[dateStr]) {
      setDateAvailability(prev => ({
        ...prev,
        [dateStr]: {
          timeSlots: [],
          isAvailable: true
        }
      }));
    }
  };

  const addDateTimeSlot = (dateStr) => {
    setDateAvailability(prev => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        timeSlots: [...(prev[dateStr]?.timeSlots || []), { start: '09:00', end: '17:00' }]
      }
    }));
  };

  const removeDateTimeSlot = (dateStr, index) => {
    setDateAvailability(prev => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        timeSlots: prev[dateStr].timeSlots.filter((_, i) => i !== index)
      }
    }));
  };

  const updateDateTimeSlot = (dateStr, index, field, value) => {
    setDateAvailability(prev => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        timeSlots: prev[dateStr].timeSlots.map((slot, i) =>
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const toggleDateAvailable = (dateStr) => {
    setDateAvailability(prev => ({
      ...prev,
      [dateStr]: {
        ...prev[dateStr],
        isAvailable: !prev[dateStr]?.isAvailable
      }
    }));
  };

  const saveDateAvailability = async (dateStr) => {
    try {
      setSaving(true);
      const dayData = dateAvailability[dateStr];
      if (!dayData) return;

      const { error } = await supabase
        .from('admin_date_availability')
        .upsert({
          availability_date: dateStr,
          time_slots: dayData.timeSlots,
          is_available: dayData.isAvailable
        }, {
          onConflict: 'availability_date'
        });

      if (error) throw error;

      setMessage({ type: 'success', text: `Availability for ${dateStr} saved successfully!` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving date availability:', error);
      setMessage({ type: 'error', text: 'Failed to save date availability' });
    } finally {
      setSaving(false);
    }
  };

  const deleteDateAvailability = async (dateStr) => {
    try {
      const { error } = await supabase
        .from('admin_date_availability')
        .delete()
        .eq('availability_date', dateStr);

      if (error) throw error;

      setDateAvailability(prev => {
        const newState = { ...prev };
        delete newState[dateStr];
        return newState;
      });

      if (selectedDate === dateStr) {
        setSelectedDate(null);
      }

      setMessage({ type: 'success', text: `Availability for ${dateStr} deleted!` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting date availability:', error);
      setMessage({ type: 'error', text: 'Failed to delete date availability' });
    }
  };

  const getDateStatus = (date) => {
    if (!date) return null;
    const dateStr = date.toISOString().split('T')[0];
    const availability = dateAvailability[dateStr];
    if (!availability) return 'default';
    if (!availability.isAvailable) return 'unavailable';
    if (availability.timeSlots && availability.timeSlots.length > 0) return 'custom';
    return 'default';
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="admin-availability">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-availability">
      <h1 className="availability-title">Manage Availability</h1>
      
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="availability-tabs">
        <button
          className={`tab-button ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          Weekly Schedule
        </button>
        <button
          className={`tab-button ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendar Overrides
        </button>
      </div>

      {activeTab === 'weekly' && (
        <div className="weekly-schedule">
          <p className="section-description">
            Set your recurring weekly availability. These times will apply to all weeks unless overridden by specific dates.
          </p>

          {DAYS_OF_WEEK.map(day => {
            const dayData = weeklySchedule[day.value] || { timeSlots: [], isActive: true };
            return (
              <div key={day.value} className="day-schedule-card">
                <div className="day-header">
                  <div className="day-title-row">
                    <h3 className="day-name">{day.label}</h3>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={dayData.isActive}
                        onChange={() => toggleDayActive(day.value)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                {dayData.isActive && (
                  <div className="time-slots-container">
                    {dayData.timeSlots.length === 0 && (
                      <p className="no-slots">No time slots set. Click "Add Time Slot" to add availability.</p>
                    )}
                    {dayData.timeSlots.map((slot, index) => (
                      <div key={index} className="time-slot-row">
                        <input
                          type="time"
                          value={slot.start}
                          onChange={(e) => updateTimeSlot(day.value, index, 'start', e.target.value)}
                          className="time-input"
                        />
                        <span className="time-separator">to</span>
                        <input
                          type="time"
                          value={slot.end}
                          onChange={(e) => updateTimeSlot(day.value, index, 'end', e.target.value)}
                          className="time-input"
                        />
                        <button
                          type="button"
                          onClick={() => removeTimeSlot(day.value, index)}
                          className="remove-slot-btn"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addTimeSlot(day.value)}
                      className="add-slot-btn"
                    >
                      + Add Time Slot
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          <button
            onClick={saveWeeklySchedule}
            disabled={saving}
            className="save-button"
          >
            {saving ? 'Saving...' : 'Save Weekly Schedule'}
          </button>
        </div>
      )}

      {activeTab === 'calendar' && (
        <div className="calendar-view">
          <p className="section-description">
            Override your weekly schedule for specific dates. Click on a date to set custom availability.
          </p>

          <div className="calendar-container">
            <div className="calendar-header">
              <button onClick={() => navigateMonth(-1)} className="month-nav-btn">‹</button>
              <h2 className="calendar-month">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h2>
              <button onClick={() => navigateMonth(1)} className="month-nav-btn">›</button>
            </div>

            <div className="calendar-grid">
              {dayNames.map(day => (
                <div key={day} className="calendar-day-header">{day}</div>
              ))}
              {getDaysInMonth(currentMonth).map((date, index) => {
                if (!date) return <div key={`empty-${index}`} className="calendar-day empty"></div>;
                const dateStr = date.toISOString().split('T')[0];
                const status = getDateStatus(date);
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                const isSelected = selectedDate === dateStr;

                return (
                  <div
                    key={dateStr}
                    className={`calendar-day ${status} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleDateClick(date)}
                  >
                    {date.getDate()}
                  </div>
                );
              })}
            </div>

            <div className="calendar-legend">
              <div className="legend-item">
                <span className="legend-color default"></span>
                <span>Uses weekly schedule</span>
              </div>
              <div className="legend-item">
                <span className="legend-color custom"></span>
                <span>Custom availability</span>
              </div>
              <div className="legend-item">
                <span className="legend-color unavailable"></span>
                <span>Unavailable</span>
              </div>
            </div>
          </div>

          {selectedDate && (
            <div className="date-availability-editor">
              <div className="editor-header">
                <h3>Availability for {new Date(selectedDate).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</h3>
                <button
                  onClick={() => setSelectedDate(null)}
                  className="close-editor-btn"
                >
                  ×
                </button>
              </div>

              {dateAvailability[selectedDate] && (
                <div className="date-editor-content">
                  <div className="availability-toggle-row">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={dateAvailability[selectedDate].isAvailable}
                        onChange={() => toggleDateAvailable(selectedDate)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span>Available on this date</span>
                  </div>

                  {dateAvailability[selectedDate].isAvailable && (
                    <div className="time-slots-container">
                      {dateAvailability[selectedDate].timeSlots.length === 0 && (
                        <p className="no-slots">No time slots set. Click "Add Time Slot" to add availability.</p>
                      )}
                      {dateAvailability[selectedDate].timeSlots.map((slot, index) => (
                        <div key={index} className="time-slot-row">
                          <input
                            type="time"
                            value={slot.start}
                            onChange={(e) => updateDateTimeSlot(selectedDate, index, 'start', e.target.value)}
                            className="time-input"
                          />
                          <span className="time-separator">to</span>
                          <input
                            type="time"
                            value={slot.end}
                            onChange={(e) => updateDateTimeSlot(selectedDate, index, 'end', e.target.value)}
                            className="time-input"
                          />
                          <button
                            type="button"
                            onClick={() => removeDateTimeSlot(selectedDate, index)}
                            className="remove-slot-btn"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addDateTimeSlot(selectedDate)}
                        className="add-slot-btn"
                      >
                        + Add Time Slot
                      </button>
                    </div>
                  )}

                  <div className="editor-actions">
                    <button
                      onClick={() => saveDateAvailability(selectedDate)}
                      disabled={saving}
                      className="save-button"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => deleteDateAvailability(selectedDate)}
                      className="delete-button"
                    >
                      Delete Override
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminAvailability;
